import useRolePermission from "entities/auth/hooks/useRolePermission";
import { FaqData } from "entities/faqs/types";
import React, { FC } from "react";
import DeleteConfirmationModal from "shared/components/DeleteConfirmationModal";
import DragNDropTable from "shared/components/DragNDropTable";
import FlexCenter from "shared/components/Layout/components/FlexCenter";
import FlexColumn from "shared/components/Layout/components/FlexColumn";
import PageHeading from "shared/components/PageHeading";
import PrimaryButton from "shared/components/PrimaryButton";
import ChangeGroupModal from "./components/ChangeGroupModal";
import FaqDetailsDrawer from "./components/FaqDetailsDrawer";
import { FaqsProvider, useFaqs } from "./context";
import { getPageModulesColumns } from "./helpers";

const FaqsContainer = () => (
  <FaqsProvider>
    <Faqs />
  </FaqsProvider>
);

const Faqs: FC = () => {
  const {
    data,
    isLoadingFaqs,
    faqsGroupTitle,
    faqDetailsDrawer,
    columnsParams,
    mutateReorderFaqsList,
    isLoadingReorderList,
    deleteModalState,
    handleDeleteFaqItem,
    isLoadingDeleteFaqItem,
    changeGroupModalState,
  } = useFaqs();

  const writeAccess = useRolePermission("faq:write");

  const columns = getPageModulesColumns(columnsParams);

  const emptyListMessage = (
    <>
      It looks like there are no Frequently Asked Questions available at the
      moment.
      <br />
      Click the "Add new FAQ" button to create the first entry
    </>
  );

  return (
    <FlexColumn>
      <PageHeading customPageTitle={faqsGroupTitle}>
        {writeAccess.allowed && (
          <FlexCenter gap="1rem" alignSelf="flex-end">
            <PrimaryButton
              title={"Add new FAQ"}
              onClick={() => faqDetailsDrawer.openDrawer()}
              size="small"
            />
          </FlexCenter>
        )}
      </PageHeading>
      <DragNDropTable<FaqData>
        items={data || []}
        columns={columns}
        reorderDisabled={!writeAccess.allowed}
        isItemsFetched={!isLoadingFaqs}
        isItemsLoading={isLoadingFaqs || isLoadingReorderList}
        noItemsMessage="No faqs found"
        emptyListMessage={emptyListMessage}
        onItemsReorder={async (faqsList) => {
          const formattedList = faqsList.map((faq, idx) => ({
            ...faq,
            order: idx + 1,
          }));
          mutateReorderFaqsList({
            faqsList: formattedList,
          });
        }}
        onRowClick={(params) => faqDetailsDrawer.openDrawer(params.id)}
      />
      <FaqDetailsDrawer />
      <ChangeGroupModal
        open={changeGroupModalState.isOpen}
        onClose={changeGroupModalState.closeModal}
      />
      <DeleteConfirmationModal
        title="Delete FAQ"
        content="Are you sure you want to delete this question?"
        applyButtonTitle="Yes, delete"
        onClose={deleteModalState.closeModal}
        open={deleteModalState.isOpen}
        handleSuccess={handleDeleteFaqItem}
        applyButtonLoading={isLoadingDeleteFaqItem}
      />
    </FlexColumn>
  );
};

export default FaqsContainer;
